import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button} from "antd";
import {SwapOutlined} from "@ant-design/icons";
import CurrencyDropdown from "../../components/select/CurrencyDropdown";
import {getRates} from "../../api/rates";

const currencies = {
  USD: {label: "Доллар США", symbol: "$"},
  RUB: {label: "Российский рубль", symbol: "₽"},
};

const formatDate = (date) => {
  const d = date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const t = date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
  return `${d}, ${t} UTC`;
};

const HomeContent = () => {
  const navigate = useNavigate();

  const [buyRate, setBuyRate] = useState(0);
  const [sellRate, setSellRate] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    document.title = "USDSeller | Главная";

    const fetchRates = () => {
      getRates().then((data) => {
        const pair = data.find(
          (r) => r.currency_from === "USD" && r.currency_to === "RUB"
        );
        if (pair) {
          setBuyRate(parseFloat(pair.buy_rate));
          setSellRate(parseFloat(pair.sell_rate));
          setUpdatedAt(new Date());
        }
      });
    };

    fetchRates();
    const interval = setInterval(fetchRates, 60_000);
    return () => clearInterval(interval);
  }, []);

  const [amount, setAmount] = useState("1.00");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("RUB");

  /*
   * Логика обменника (без ratio_rate):
   *  USD→RUB  клиент продаёт USD  →  rate = buy_rate
   *  RUB→USD  клиент покупает USD →  rate = 1/sell_rate
   */
  const rate = useMemo(() => {
    if (from === "USD" && to === "RUB") return buyRate;
    if (from === "RUB" && to === "USD") return sellRate > 0 ? 1 / sellRate : 0;
    return 0;
  }, [from, to, buyRate, sellRate]);

  const inverse = useMemo(() => {
    if (rate === 0) return 0;
    return 1 / rate;
  }, [rate]);

  /* ── форматирование курса ── */
  const formatted = rate.toFixed(6);
  const [intPart, decimalPart] = formatted.split(".");
  const mainDecimals = decimalPart.slice(0, 2);
  const fadedDecimals = decimalPart.slice(2);

  const symbol = useMemo(() => currencies[from]?.symbol ?? "", [from]);
  const fromLabel = useMemo(() => currencies[from]?.label ?? from, [from]);
  const toLabel = useMemo(() => currencies[to]?.label ?? to, [to]);

  const updatedAtText = updatedAt ? formatDate(updatedAt) : "—";

  const handleFromChange = useCallback((val) => {
    if (val === to) setTo(from);
    setFrom(val);
  }, [from, to]);

  const handleToChange = useCallback((val) => {
    if (val === from) setFrom(to);
    setTo(val);
  }, [from, to]);

  const handleSwitch = useCallback(() => {
    setFrom((prevFrom) => {
      setTo(prevFrom);
      return to;
    });
  }, [to]);

  return (
    <>
      <div className="home-main max-w-[93%] lg:max-w-[1500px]">
        <div className="home-main-title">
          <span className="home-main-title-text-h1 heading-1">
            Приветствуем Вас на USD Seller проекте
          </span>
          <p className="home-main-title-text-p body-1">
            Мы помогаем перевести денежные средства из и в США, а также обменять
            криптовалюту в США по выгодным курсам. Помимо этого, мы предлагаем
            сервис «Счет в США», где Вы сможете подключить процессинг платежей от
            физических и юридических лиц, получая далее средства в своей
            национальной валюте или криптовалюте. Подробнее можно прочитать на
            странице «Счет в США».
          </p>
        </div>

        <div className="home-main-inside">
          <div className="home-main-inside-header mt-3">
            <span className="home-main-inside-text-h1 heading-2">
              Актуальные курсы обмена валют онлайн
            </span>
            <p className="home-main-inside-text-p body-1">
              Посмотрите текущие курсы обмена. Данные обновляются в реальном
              времени.
            </p>
          </div>

          {/* Exchange */}
          <div className="exchange-controls">
            {/* Amount */}
            <div className="content-border exchange-field lg:mr-5 exchange-field-amount px-6 pt-2 pb-2">
              <label className="content-label body-2">Сумма</label>
              <div className="amount-display subtitle-1">
                <span>{symbol}</span>
                <input
                  inputMode="decimal"
                  className="amount-input subtitle-1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>

            {/* From / Swap / To */}
            <div className="exchange-currency-group lg:max-w-[1000px]">
              <div className="content-border exchange-field px-6 pt-2 pb-2">
                <label className="content-label body-2">Из</label>
                <CurrencyDropdown value={from} onChange={handleFromChange}/>
              </div>

              <div className="exchange-swap-wrapper">
                <button
                  className="exchange-swap-btn"
                  onClick={handleSwitch}
                  type="button"
                >
                  <SwapOutlined className="exchange-swap-icon"/>
                </button>
              </div>

              <div className="content-border exchange-field px-6 pt-2 pb-2">
                <label className="content-label body-2">В</label>
                <CurrencyDropdown value={to} onChange={handleToChange}/>
              </div>
            </div>
          </div>
          {/* End Exchange */}

          {/* Rate Info */}
          <div className="home-main-inside-footer">
            <div className="exchange-rate-info">
              <div className="exchange-rate-text subtitle-3">
                1.00{" "}
                <span className="exchange-rate-base-short">{from}</span>
                <span className="exchange-rate-base-full">{fromLabel}</span> =
              </div>
              <div className="exchange-rate-value-container heading-3">
                <div className="exchange-rate-value">
                  {intPart}.{mainDecimals}
                  <span className="exchange-rate-faded">{fadedDecimals}</span>
                </div>
                <div className="exchange-rate-currency">{toLabel}</div>
              </div>
              <div className="exchange-rate-conversion-text body-2">
                1 {to} = {inverse.toFixed(6)} {from}
              </div>
            </div>

            <div className="home-main-inside-footer-second">
              <p className="home-main-inside-footer-text-p body-2">
                Конвертация {fromLabel} в{" "}
                {toLabel}. Последнее обновление{" "}
                <span>{updatedAtText}</span>
              </p>
              <Button
                type="primary"
                size="large"
                onClick={() => navigate("/all-curses")}
                className="home-main-inside-footer-allcurses"
              >
                Смотреть все курсы
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomeContent;
